import { Link } from 'react-router-dom';

export function Tos() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-3 h-16 flex items-center">
          <Link to="/" className="text-lg font-semibold">
            Brand Check
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto prose prose-gray dark:prose-invert">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Terms of Service
          </h1>

          <p className="text-sm text-gray-600 mb-6">
            <strong>Last Updated: September 8, 2025</strong>
          </p>

          <p className="mb-4 leading-relaxed">
            If you signed a separate Cover Page to access the Product with the
            same account, and that agreement has not ended, the terms below do
            not apply to you. Instead, your separate Cover Page applies to your
            use of the Product.
          </p>

          <p className="mb-4 leading-relaxed">
            This Agreement is between Hacket Services OÜ and the company or
            person accessing or using the Product. This Agreement consists of:
            (1) the Order Form below and (2) the Framework Terms defined below.
          </p>

          <p className="mb-6 leading-relaxed">
            If you are accessing or using the Product on behalf of your company,
            you represent that you are authorized to accept this Agreement on
            behalf of your company. By signing up, accessing, or using the
            Product, Customer indicates its acceptance of this Agreement and
            agrees to be bound by the terms and conditions of this Agreement.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
            Cover Page
          </h2>

          <p className="text-lg font-medium text-gray-800 mb-4">
            <em>Order Form</em>
          </p>

          <p className="mb-4 leading-relaxed">
            <strong className="text-gray-900">Framework Terms:</strong> This
            Order Form incorporates and is governed by the Framework Terms that
            are made up of the Key Terms below and the Common Paper{' '}
            <a
              href="https://commonpaper.com/standards/cloud-service-agreement/2.1/"
              className="text-blue-600 hover:text-blue-800 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cloud Service Agreement Standard Terms Version 2.1
            </a>
            , which are incorporated by reference. Any modifications to the
            Standard Terms made in the Cover Page will control over conflicts
            with the Standard Terms. Capitalized words have the meanings given
            in the Cover Page or the Standard Terms.
          </p>

          <div className="space-y-4 mb-6">
            <p className="leading-relaxed">
              <strong className="text-gray-900">Cloud Service:</strong> an
              AI-powered influencer analysis platform that helps users vet
              social media creators by analyzing online profiles for promoted
              brands and potential conflicts of interest.
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Order Date:</strong> The
              Effective Date
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Subscription Period:</strong> 1
              month(s)
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Cloud Service Fees:</strong>
              Certain parts of the Product have different pricing plans, which
              are available at Provider's [pricing
              page](https://usebrandcheck.com/#pricing). Customer will pay
              Provider the applicable Fees based on the Product tier and
              Customer's usage. Provider may update Product pricing by giving at
              least 5 days notice to Customer (including by email or
              notification within the Product), and the change will apply in the
              next Subscription Period.
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Payment Process:</strong>
              Automatic payment: Customer authorizes Provider to bill and charge
              Customer's payment method on file Monthly for immediate payment or
              deduction without further approval.
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">
                Non-Renewal Notice Period:
              </strong>{' '}
              At least 1 days before the end of the current Subscription Period.
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Use Limitations:</strong> Each
              subscription plan includes a fixed number of usage credits per
              billing period. Credits are consumed based on the number of
              influencer profiles and posts analyzed, as well as the content
              volume of those posts. If a customer's usage exceeds the credits
              included in their plan, the excess usage will be billed at the
              applicable overage rate at the end of the billing period.
              Customers are responsible for monitoring their own usage and any
              resulting charges.
            </p>
          </div>

          <p className="text-lg font-medium text-gray-800 mb-4">
            <em>Key Terms</em>
          </p>

          <div className="space-y-4 mb-6">
            <p className="leading-relaxed">
              <strong className="text-gray-900">Customer:</strong> The company
              or person who accesses or uses the Product. If the person
              accepting this Agreement is doing so on behalf of a company, all
              use of the word "Customer" in the Agreement will mean that
              company.
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Provider:</strong> Hacket
              Services OÜ
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Effective Date:</strong> The
              date Customer first accepts this Agreement.
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Governing Law:</strong> The laws
              of the State of Delaware
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Chosen Courts:</strong> The
              state or federal courts located in Delaware
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Covered Claims:</strong>
            </p>

            <div className="ml-4 space-y-4">
              <p className="leading-relaxed">
                <strong className="text-gray-900">
                  Provider Covered Claims:
                </strong>{' '}
                Provider will not be responsible for any claims arising from
                third-party social media content, scraping activities,
                AI-generated analysis, or customer use of the Service. For
                clarity, Provider will not cover any claims arising out of or
                related to: (i) third-party social media content (including
                embedded posts) or the availability/changes of third-party
                platforms or APIs (e.g., Instagram/TikTok); (ii) scraping or
                acquisition of third-party content and any alleged breaches of
                third-party terms of service; (iii) AI-generated analyses,
                inferences, classifications, or reports, or any decisions or
                actions taken in reliance on them; (iv) Customer content, data,
                prompts, instructions, or configurations; (v) Customer's
                marketing campaigns, influencer relationships, or brand
                disputes; (vi) alleged defamation, disparagement,
                misrepresentation, violation of rights of publicity or privacy,
                or trademark/brand confusion based on analyzed content or
                outputs; and (vii) Customer's violation of laws, regulations, or
                third-party terms.
              </p>

              <p className="leading-relaxed">
                <strong className="text-gray-900">
                  Customer Covered Claims:
                </strong>{' '}
                Customer will cover claims arising from: (a) any content, data,
                or materials Customer uploads, inputs, or processes through the
                Service; (b) Customer's use of the Service to analyze, display,
                or otherwise process third-party social media content (including
                scraping and embedding); (c) Customer's campaigns, influencer
                engagements, marketing decisions, or brand relationships; (d)
                any use of or reliance on AI-generated outputs, analyses, or
                reports; and (e) Customer's breach of applicable laws,
                regulations, or third-party platform/API terms of service.
              </p>
            </div>

            <p className="leading-relaxed">
              <strong className="text-gray-900">General Cap Amount:</strong>
              The fees paid or payable by Customer to provider in the 12 month
              period immediately before the claim
            </p>

            <p className="leading-relaxed">
              <strong className="text-gray-900">Notice Address:</strong>
            </p>

            <div className="ml-4 space-y-2">
              <p className="leading-relaxed">
                For Provider: info@usehacket.com
              </p>
              <p className="leading-relaxed">
                For Customer: The main email address on Customer's account
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
            Payments, Refunds & Credits
          </h2>

          <div className="ml-4 space-y-4 mb-6">
            <p className="text-lg font-medium text-gray-800 mb-4">
              <em>Refunds & Credits</em>
            </p>

            <div className="space-y-4">
              <p className="leading-relaxed">
                <strong className="text-gray-900">
                  Subscriptions; Renewal; Cancellation.
                </strong>{' '}
                Subscriptions renew automatically for successive billing periods
                unless canceled before the end of the then-current period. You
                may cancel at any time; cancellation takes effect at the end of
                the current period. Fees already paid are non-refundable and we
                do not provide credits or refunds for partial periods.
              </p>

              <p className="leading-relaxed">
                <strong className="text-gray-900">Credits.</strong> "Credits"
                are prepaid units that can be used to access certain features.
                Credits are not currency, have no cash value, are
                non-transferable, and are non-refundable. Credits used to access
                Services are consumed and cannot be returned.
              </p>

              <p className="leading-relaxed">
                <strong className="text-gray-900">Refunds.</strong> All fees and
                purchases (including credits and subscription fees) are final
                and non-refundable, except where a refund is required by
                applicable law (for example, where mandatory consumer rights
                apply). If a refund is issued, we may deduct the value of any
                credits or Services already consumed.
              </p>

              <p className="leading-relaxed">
                <strong className="text-gray-900">Trials & Promotions.</strong>{' '}
                If you convert from a free trial or promotional offer, you will
                be charged at the then-current rate at the end of the
                promotional period unless you cancel beforehand. Promotional
                terms may impose additional restrictions; all promotional fees
                are non-refundable unless required by law.
              </p>

              <p className="leading-relaxed">
                <strong className="text-gray-900">Chargebacks.</strong> You
                agree to contact us to resolve any billing issue before
                initiating a chargeback. We may suspend or terminate access for
                fraudulent or improper chargebacks.
              </p>
            </div>
          </div>

          <p className="text-lg font-medium text-gray-800 mb-4">
            <em>Changes to the Standard Terms</em>
          </p>

          <div className="space-y-4">
            <p className="leading-relaxed">
              Customer acknowledges and agrees that fees include metered usage
              charges based on the number of creator profiles and posts analyzed
              and the content volume processed. Usage accrues as the Service is
              used and is non-cancellable and nonrefundable once incurred.
              Included credits per billing period offset usage first; any excess
              usage is billed at the then-current overage rates. Provider's
              metering and server logs are conclusive for calculating usage and
              fees unless Customer provides verifiable contrary evidence within
              fifteen (15) days of invoice.
            </p>

            <p className="leading-relaxed">
              Customer must maintain a current, valid Payment Method and
              authorizes Provider and its billing/Merchant-of-Record vendors
              (e.g., Paddle) to (a) place preauthorization holds, (b)
              automatically charge all fees, taxes, and overages as they become
              due, and (c) reattempt failed charges and send dunning
              communications. Chargebacks are treated as unpaid amounts.
            </p>

            <p className="leading-relaxed">
              Provider may immediately suspend the Service (including halting
              new analyses) if the Payment Method is invalid, charges are
              declined, credits are depleted, or any amount is overdue.
              Suspension does not waive amounts already incurred. Provider may
              require prepayment, a deposit, or automatic top-ups before
              resuming Service.
            </p>

            <p className="leading-relaxed">
              Past-due amounts accrue interest at the lesser of 1.5% per month
              or the maximum rate permitted by law, plus all reasonable costs of
              collection (including attorneys' fees). Customer may not withhold,
              counterclaim, or set off fees.
            </p>

            <p className="leading-relaxed">
              Fees are exclusive of taxes; Customer is responsible for all
              applicable taxes (excluding Provider's income taxes). Currency
              conversions and payment processor fees are borne by Customer.
              Payment obligations survive termination and suspension.
            </p>

            <p className="leading-relaxed">
              Any fee or usage dispute must be submitted in writing within
              fifteen (15) days of the invoice date; otherwise the invoice is
              deemed accepted and final. Provider may require Customer to enable
              automatic credit top-ups or maintain a prepaid balance. Provider
              will not process additional analyses if credits are insufficient.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
